import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    onSnapshot,
    serverTimestamp,
    setDoc,
    Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '../firebase.js';

// The one service mailbox every Welcome Email is sent from; set in the functions.
export const WELCOME_EMAIL_FROM = 'ARIS Robotics <service@aris-robotics.com>';

const templatesCollection = () => collection(db, 'welcome_email_templates');

export function subscribeToWelcomeEmailTemplates(onTemplates, onError) {
    return onSnapshot(
        templatesCollection(),
        (snapshot) =>
            onTemplates(
                snapshot.docs
                    .map((snap) => ({ id: snap.id, ...snap.data() }))
                    .sort((a, b) => String(a.name).localeCompare(String(b.name), 'da')),
            ),
        onError,
    );
}

// Creates the template when it has no id yet; returns the id either way.
export async function saveWelcomeEmailTemplate({ id, name, subject, body, reply_to }, updatedBy) {
    const data = {
        name: name.trim(),
        subject: subject.trim(),
        body,
        reply_to: reply_to.trim(),
        updated_at: serverTimestamp(),
        updated_by: updatedBy,
    };
    if (id) {
        await setDoc(doc(templatesCollection(), id), data);
        return id;
    }
    const created = await addDoc(templatesCollection(), data);
    return created.id;
}

export function deleteWelcomeEmailTemplate(id) {
    return deleteDoc(doc(templatesCollection(), id));
}

const provisionUsersCall = httpsCallable(functions, 'provisionUsers', { timeout: 300000 });
const sendWelcomeEmailCall = httpsCallable(functions, 'sendWelcomeEmail');
const redeemWelcomeLinkCall = httpsCallable(functions, 'redeemWelcomeLink');

export async function provisionUsers({ people, access, templateId, target }) {
    const result = await provisionUsersCall({ people, access, template_id: templateId, target });
    return result.data;
}

export async function sendWelcomeEmail({ uid, templateId, target }) {
    const result = await sendWelcomeEmailCall({ uid, template_id: templateId, target });
    return result.data;
}

export async function redeemWelcomeLink(token) {
    const result = await redeemWelcomeLinkCall({ token });
    return result.data;
}

// Every refusal the provisioning functions can give, by `details.reason`.
export const PROVISIONING_ERROR_MESSAGES = {
    not_admin: 'Kun admins kan oprette brugere.',
    unauthenticated: 'Du er ikke logget ind.',
    no_people: 'Tilføj mindst én person.',
    too_many_people: 'Højst 50 personer ad gangen.',
    invalid_email: 'En af emailadresserne er ikke gyldig.',
    duplicate_email: 'En emailadresse står flere gange på listen.',
    unknown_template: 'Skabelonen findes ikke længere.',
    invalid_template: 'Skabelonen er ikke gyldig. Ret den under E-mailskabeloner.',
    invalid_target: 'Vælg hvor brugeren skal sendes hen.',
    unknown_company: 'En af virksomhederne findes ikke.',
    unknown_waste_receiver: 'En af affaldsmodtagerne findes ikke.',
    unknown_user: 'Brugeren findes ikke.',
    no_email: 'Brugeren har ingen emailadresse.',
    internal: 'Noget gik galt. Prøv igen.',
};

export function describeProvisioningError(error) {
    return PROVISIONING_ERROR_MESSAGES[error?.details?.reason] || PROVISIONING_ERROR_MESSAGES.internal;
}

const toDate = (value) => (value instanceof Timestamp ? value.toDate() : value instanceof Date ? value : null);

// The latest entry in a user's welcome email history, or null.
export function lastWelcomeEmail(welcomeEmails) {
    if (!Array.isArray(welcomeEmails) || welcomeEmails.length === 0) return null;
    return welcomeEmails
        .map((entry) => ({ ...entry, sent_at: toDate(entry.sent_at) }))
        .filter((entry) => entry.sent_at)
        .sort((a, b) => b.sent_at - a.sent_at)[0] || null;
}
